import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API from '../../api';
import { BASE_URL } from "../../config";
import DateInput from "../../components/DateInput";
import "../../Styles/Profile.css";
import AddressAutocomplete from '../../components/AddressAutocomplete';
import PhoneInput, { isValidPhoneNumber, getCountryCallingCode } from 'react-phone-number-input';
import { isValidEmail } from '../../utils/email';
import { isCanadianProvince, saveRegistrationProvince } from '../../utils/registrationProvince';
import { showSuccess, showError } from '../../utils/dialog';
import {
  STAFF_DOCUMENT_ACCEPT,
  validateStaffDocumentFile,
} from '../../utils/staffDocumentValidation';
import 'react-phone-number-input/style.css';
import talentLogo from "../../assets/talentlogo2.png";
import { FaUpload, FaDownload } from "react-icons/fa";
import StaffAvatarCircle from "../../components/StaffAvatarCircle";

const emptyAddress = {
  name: "",
  street: "",
  city: "",
  region: "",
  zip: "",
  country: "",
};

const normalizeAddress = (value) => {
  if (!value) return { ...emptyAddress };
  if (typeof value === "string") {
    return { ...emptyAddress, street: value };
  }
  if (typeof value === "object") {
    return {
      ...emptyAddress,
      ...value,
      street: value.street ?? value.line1 ?? "",
      city: value.city ?? "",
      region: value.region ?? value.province ?? "",
      zip: value.zip ?? value.postal_code ?? "",
      country: value.country ?? "",
      name: value.name ?? "",
    };
  }
  return { ...emptyAddress };
};

const hasAddressValue = (address) => {
  if (!address) return false;
  if (typeof address === "string") return String(address).trim() !== "";
  return Boolean(
    String(address.street || "").trim() ||
      String(address.city || "").trim() ||
      String(address.region || "").trim() ||
      String(address.zip || "").trim() ||
      String(address.country || "").trim()
  );
};

// Temporary residency statuses: permit document upload + its expiry date are required
const PERMIT_RESIDENCY_STATUSES = ["work_permit", "study_permit", "refugee"];
const isResidencyExpiryMet = (data) =>
  !PERMIT_RESIDENCY_STATUSES.includes(data.residency) || !!data.residency_document_expiry;

// SIN may be typed with spaces or dashes (e.g. 123-456-789); validation uses digits only
const normalizeSin = (value) => String(value || "").replace(/[\s-]/g, "");
const isValidSin = (value) => /^\d{9}$/.test(normalizeSin(value));

// Either a valid SIN is provided, or the staff member has agreed to the disclaimer
const isSinRequirementMet = (data) =>
  normalizeSin(data.sin) ? isValidSin(data.sin) : data.sin_disclaimer_agreed === true;

const SIN_DISCLAIMER_TEXT =
  "Providing your SIN at this time is optional. However, once you begin working, you must provide " +
  "a copy of your SIN for payroll purposes. If your SIN is not provided, we will be unable to process your payment.";
const SIN_DISCLAIMER_AGREE_TEXT =
  "I understand and wish to continue without providing my SIN at this time.";

export default function Profile({ accountId = "1", contentId = "profile" }) {
  const navigate = useNavigate();
  const currentAccountId = localStorage.getItem("accountId") || accountId;
  const [me, setMe] = useState(null);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [mobile, setMobile] = useState("");
  const [emergency, setEmergency] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const requiredFields = [
    "first_name",
    "last_name",
    "email",
    "mobile",
    "address",
    "emergency_name",
    "emergency_number",
    "residency",
  ];

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    middle_name: "",
    preferred_name: "",
    email: "",
    gender: "",
    dob: "",
    mobile: "",
    address: { ...emptyAddress },
    emergency_name: "",
    emergency_number: "",
    residency: "",
    residency_document: "",
    residency_document_name: "",
    residency_document_expiry: "",
    sin: "",
    sin_disclaimer_agreed: false,
    profile_picture: "",
    profile_picture_preview: ""
  });

  const [status, setStatus] = useState("incomplete");

  // Ensure calling codes are visible initially when fields are empty (without overwriting loaded form)
  useEffect(() => {
    let changed = false;
    const code = `+${getCountryCallingCode('CA')}`;
    const nextMobile = !mobile ? code : mobile;
    const nextEmergency = !emergency ? code : emergency;
    if (!mobile) {
      setMobile(code);
      changed = true;
    }
    if (!emergency) {
      setEmergency(code);
      changed = true;
    }
    if (changed) {
      setFormData(prev => {
        const merged = { ...prev };
        if (!prev.mobile) merged.mobile = nextMobile;
        if (!prev.emergency_number) merged.emergency_number = nextEmergency;
        localStorage.setItem(`form_${currentAccountId}_${contentId}`, JSON.stringify(merged));
        return merged;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(`form_${currentAccountId}_${contentId}`);
    if (saved) {
      const parsed = JSON.parse(saved);
      parsed.address = normalizeAddress(parsed.address);
      // Always ensure preview mirrors base64 image for reliability across sessions
      if (parsed.profile_picture) {
        parsed.profile_picture_preview = parsed.profile_picture;
      }
      localStorage.setItem(`form_${currentAccountId}_${contentId}`, JSON.stringify(parsed));
      setFormData(parsed);
      setMe(parsed);

      setMobile(parsed.mobile || "");
      setEmergency(parsed.emergency_number || "");

      // residency document fields (if present)
      setFormData((prev) => ({ ...prev, residency_document: parsed.residency_document || prev.residency_document || "", residency_document_name: parsed.residency_document_name || prev.residency_document_name || "" }));

      const values = Object.values(parsed).map((v) => String(v || "").trim());
      const allEmpty = values.every((v) => v === "");
      const requiredFilled =
        requiredFields.every((f) => String(parsed[f] || "").trim() !== "") &&
        isSinRequirementMet(parsed) &&
        isResidencyExpiryMet(parsed) &&
        isCanadianProvince(parsed.address?.region);

      let initialStatus;
      if (allEmpty) initialStatus = "incomplete";
      else if (requiredFilled) initialStatus = "complete";
      else initialStatus = "in-progress";

      setStatus(initialStatus);
      localStorage.setItem(`status_${currentAccountId}_${contentId}`, initialStatus);
    } else {
      // No local draft — try token-based profile first, then accountId profile, then signup fallbacks
      (async () => {
        let initial = { first_name: "", last_name: "", email: "", mobile: "", preferred_name: "" };
        try {
          // Prefer token-based endpoint so this works across browsers/sessions where accountId might be missing
          const respMe = await API.get('/api/registration/me/profile');
          const profileMe = respMe.data || {};
          // Load ALL fields from API, mapping DB fields to frontend field names
          initial = {
            first_name: profileMe.first_name || localStorage.getItem('signup_first_name') || "",
            last_name: profileMe.last_name || localStorage.getItem('signup_last_name') || "",
            middle_name: profileMe.middle_name || "",
            preferred_name: profileMe.preferred_name || "",
            email: profileMe.email || localStorage.getItem('signup_email') || "",
            mobile: profileMe.mobile || profileMe.phone_mobile || "",
            gender: profileMe.gender || "",
            dob: profileMe.dob || profileMe.birthdate || "",
            emergency_name: profileMe.emergency_name || "",
            emergency_number: profileMe.emergency_number || profileMe.emergency_phone || "",
            residency: profileMe.residency || profileMe.residency_status || "",
            sin: profileMe.sin || "",
            sin_disclaimer_agreed: profileMe.sin_consent === "declined",
            address: normalizeAddress(profileMe.address || profileMe.address_id),
            profile_picture: profileMe.profile_picture || "",
            profile_picture_preview: profileMe.profile_picture || ""
          };
        } catch (errMe) {
          try {
            // Fallback to account-specific profile if token route fails
            const resp = await API.get(`/api/registration/${currentAccountId}/profile`);
            const profile = resp.data || {};
            // Load ALL fields from API, mapping DB fields to frontend field names
            initial = {
              first_name: profile.first_name || localStorage.getItem('signup_first_name') || "",
              last_name: profile.last_name || localStorage.getItem('signup_last_name') || "",
              middle_name: profile.middle_name || "",
              preferred_name: profile.preferred_name || "",
              email: profile.email || localStorage.getItem('signup_email') || "",
              mobile: profile.mobile || profile.phone_mobile || "",
              gender: profile.gender || "",
              dob: profile.dob || profile.birthdate || "",
              emergency_name: profile.emergency_name || "",
              emergency_number: profile.emergency_number || profile.emergency_phone || "",
              residency: profile.residency || profile.residency_status || "",
              sin: profile.sin || "",
              sin_disclaimer_agreed: profile.sin_consent === "declined",
              address: normalizeAddress(profile.address || profile.address_id),
              profile_picture: profile.profile_picture || "",
              profile_picture_preview: profile.profile_picture || ""
            };
          } catch (err) {
            // Final fallback: use signup-stored values (cross-browser signup -> registration)
            initial = {
              first_name: localStorage.getItem('signup_first_name') || "",
              last_name: localStorage.getItem('signup_last_name') || "",
              email: localStorage.getItem('signup_email') || "",
            };
          }
        }

        setMe(initial);
        setFormData((prev) => ({ ...prev, ...initial }));
        setMobile(initial.mobile || mobile);
        try {
          // update local draft and status to in-progress so UI remains consistent
          localStorage.setItem(`form_${currentAccountId}_${contentId}`, JSON.stringify({ ...formData, ...initial }));
        } catch (e) {
          // ignore localStorage failures
        }
        setStatus("in-progress");
        localStorage.setItem(`status_${currentAccountId}_${contentId}`, "in-progress");
      })();
    }
  }, [contentId, currentAccountId]);

  // If user types an email (and not authenticated), attempt public lookup to prefill name
  useEffect(() => {
    const email = (formData.email || "").trim();
    if (!email) return;
    // don't overwrite if names already present
    if (formData.first_name || formData.last_name) return;
    // Simple email format guard
    if (!isValidEmail(email)) return;

    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/api/registration/public-profile?email=${encodeURIComponent(email)}`);
        if (cancelled) return;
        const profile = resp.data || {};
        if (profile.first_name || profile.last_name) {
          const updated = { ...formData };
          if (profile.first_name) updated.first_name = profile.first_name;
          if (profile.last_name) updated.last_name = profile.last_name;
          setFormData(updated);
          localStorage.setItem(`form_${currentAccountId}_${contentId}`, JSON.stringify(updated));
        }
      } catch (err) {
        // ignore not found / errors
      }
    })();

    return () => { cancelled = true; };
  }, [formData.email]);

  const handleChange = (e) => {
    const { name, type, checked } = e.target;
    const value = type === "checkbox" ? checked : e.target.value;
    const updatedData = { ...formData, [name]: value };
    // Disclaimer only applies when no SIN is given; entering a SIN withdraws it
    if (name === "sin" && normalizeSin(value)) updatedData.sin_disclaimer_agreed = false;
    setFormData(updatedData);
    localStorage.setItem(`form_${currentAccountId}_${contentId}`, JSON.stringify(updatedData));

    // Clear field-level error if now valid
    setFieldErrors((prev) => {
      // SIN and disclaimer are one requirement: satisfying either clears both errors
      if ((name === "sin" || name === "sin_disclaimer_agreed") && isSinRequirementMet(updatedData)) {
        if (!prev.sin && !prev.sin_disclaimer_agreed) return prev;
        const next = { ...prev };
        delete next.sin;
        delete next.sin_disclaimer_agreed;
        return next;
      }
      if (!prev[name]) return prev;
      const next = { ...prev };
      const trimmed = String(value || "").trim();
      if (name === "email") {
        if (trimmed && isValidEmail(trimmed)) delete next[name];
      } else if (name === "sin") {
        if (isValidSin(trimmed)) delete next[name];
      } else if (trimmed) {
        delete next[name];
      }
      return next;
    });

    const requiredValues = requiredFields.map((f) =>
      f === "address"
        ? hasAddressValue(updatedData.address)
          ? "filled"
          : ""
        : String(updatedData[f] || "").trim()
    );
    const allEmpty = requiredValues.every((v) => v === "");
    const allFilled =
      requiredValues.every((v) => v !== "") &&
      isSinRequirementMet(updatedData) &&
      isResidencyExpiryMet(updatedData) &&
      isCanadianProvince(updatedData.address?.region);

    let newStatus = "in-progress";
    if (allEmpty) newStatus = "incomplete";
    else if (allFilled) newStatus = "complete";

    setStatus(newStatus);
    localStorage.setItem(`status_${currentAccountId}_${contentId}`, newStatus);

    // NOTE: Do not POST status on every change; status should be synced only when the user clicks SAVE.
  };

  const [uploading, setUploading] = useState(false);

  const handleResidencyFile = async (file) => {
    if (!file) return;
    const validationMessage = validateStaffDocumentFile(file);
    if (validationMessage) {
      showError(validationMessage);
      return;
    }

    // Ensure residency type is selected so backend knows which account column to update
    const validTypes = ['work_permit', 'study_permit', 'refugee'];
    let docType = validTypes.includes(formData.residency) ? formData.residency : null;
    if (docType === 'refugee') {
      docType = 'work_permit';
    }
    if (!docType) {
      setFieldErrors((prev) => ({ ...(prev || {}), residency_document: 'Please select Residency Status before uploading a document' }));
      return;
    }

    const fd = new FormData();
    fd.append('file', file);
    fd.append('docType', docType);

    try {
      setUploading(true);

      // POST multipart to the new backend endpoint. Use axios directly so we can send FormData.
      const resp = await axios.post(`${BASE_URL}/api/registration/${currentAccountId}/upload-and-link`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const data = resp.data || {};
      if (!data || !data.file_id) {
        setErr('Upload failed: server did not return file id');
        return;
      }

      // Persist the returned file_id instead of base64. Also store preview URL if available.
      const updated = {
        ...formData,
        residency_document: data.file_id,
        residency_document_name: file.name,
        residency_document_url: data.url || data.s3Key || ''
      };

      setFormData(updated);
      try { localStorage.setItem(`form_${currentAccountId}_${contentId}`, JSON.stringify(updated)); } catch (e) { }

      // clear any existing field error for residency_document
      setFieldErrors((prev) => {
        if (!prev || !prev.residency_document) return prev || {};
        const next = { ...prev };
        delete next.residency_document;
        return next;
      });
    } catch (err) {
      console.error('Residency upload failed', err);
      setErr('File upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = () => {
    if (!formData.residency_document_url) return;
    window.open(formData.residency_document_url, "_blank");
  };

  // Show immediate required error when a permit type is selected but no file uploaded
  useEffect(() => {
    const r = (formData.residency || '').trim();
    if ((r === 'work_permit' || r === 'study_permit' || r === 'refugee') && !formData.residency_document) {
      const permitName = r === 'study_permit' ? 'Study Permit' : 'Work Permit';
      const msg = `Please upload your ${permitName} document`;
      setFieldErrors((prev) => ({ ...(prev || {}), residency_document: msg }));
    } else {
      setFieldErrors((prev) => {
        if (!prev || !prev.residency_document) return prev || {};
        const next = { ...prev };
        delete next.residency_document;
        return next;
      });
    }
  }, [formData.residency, formData.residency_document]);

  // Debounced check for duplicate mobile phone
  useEffect(() => {
    const rawDigits = String(mobile || "").replace(/\D/g, "");
    if (!mobile || rawDigits.length < 10) {
      setFieldErrors((prev) => {
        if (!prev || prev.mobile !== "This phone number already exists in the system") return prev;
        const next = { ...prev };
        delete next.mobile;
        return next;
      });
      return;
    }

    let isCancelled = false;
    const timer = setTimeout(async () => {
      try {
        const exclude = currentAccountId ? `&excludeAccountId=${currentAccountId}` : "";
        const res = await API.get(`/api/personnel/check-phone?phone=${encodeURIComponent(mobile)}${exclude}`);
        if (!isCancelled && res.data && res.data.isDuplicate) {
          setFieldErrors((prev) => ({
            ...prev,
            mobile: "This phone number already exists in the system",
          }));
        } else if (!isCancelled) {
          setFieldErrors((prev) => {
            if (!prev || prev.mobile !== "This phone number already exists in the system") return prev;
            const next = { ...prev };
            delete next.mobile;
            return next;
          });
        }
      } catch (err) {
        console.warn("Profile phone check error:", err);
      }
    }, 350);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [mobile, currentAccountId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");
    setFieldErrors({});

    // Build per-field validation
    const errors = {};
    const getVal = (key) => String((key === "mobile"
      ? mobile
      : key === "emergency_number"
        ? emergency
        : key === "address"
          ? (hasAddressValue(formData.address) ? "filled" : "")
          : formData[key]) || "").trim();

    // Required checks
    requiredFields.forEach((key) => {
      const value = getVal(key);
      if (!value) {
        errors[key] = "This field is required";
      }
    });

    // SIN is required for every Residency Status, unless the disclaimer is agreed to instead
    if (getVal('residency')) {
      if (getVal('sin')) {
        if (!isValidSin(getVal('sin'))) {
          errors.sin = "Please enter a valid 9-digit Social Insurance Number";
        }
      } else if (!formData.sin_disclaimer_agreed) {
        errors.sin = "Enter your SIN, or read the disclaimer below and tick the checkbox to continue without it";
        errors.sin_disclaimer_agreed = "Required if you are not providing your SIN";
      }
    }

    // If residency requires a document, validate presence
    const residencyVal = (formData.residency || '').trim();
    if ((residencyVal === 'work_permit' || residencyVal === 'study_permit' || residencyVal === 'refugee') && !formData.residency_document) {
      errors.residency_document = `Please upload your ${residencyVal === 'work_permit' ? 'Work Permit' : residencyVal === 'study_permit' ? 'Study Permit' : 'Work Permit'} document`;
    }
    if (!isResidencyExpiryMet(formData)) {
      errors.residency_document_expiry = "Document Expiry Date is required";
    }

    // Province is mandatory (TD form / Smart Serve depend on it)
    if (!errors.address && !isCanadianProvince(formData.address?.region)) {
      errors.address = "Province is required: please select a Canadian province or territory in your address";
    }

    // Specific format checks
    if (getVal("email") && !isValidEmail(getVal("email"))) {
      errors.email = "Please enter a valid email address";
    }
    if (getVal("mobile") && !isValidPhoneNumber(mobile)) {
      errors.mobile = "Please enter a valid mobile number";
    }
    if (getVal("emergency_number") && !isValidPhoneNumber(emergency)) {
      errors.emergency_number = "Please enter a valid emergency number";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setErr("⚠️ Please fix the highlighted fields.");
      return;
    }

    try {
      // Send form data along with the completion status - use API so auth/credentials are included
      await API.post(`/api/registration/${currentAccountId}/items/${contentId}/complete`, {
        status: 'complete',
        formData: formData
      });

      // Also update the status endpoint for consistency
      await API.post(`/api/registration/${currentAccountId}/status/${contentId}`, {
        status: 'complete',
        formData: formData
      });
    } catch (err) {
      console.error("Backend update failed", err);

      // Extract error message from API response
      const errorResponse = err.response?.data;
      const errorMessage = errorResponse?.message || errorResponse?.error || "Failed to save data. Please try again.";

      // Check if error is related to duplicate mobile phone or SIN
      if (
        errorMessage.toLowerCase().includes('already exists') ||
        errorResponse?.field === 'mobile'
      ) {
        setFieldErrors((prev) => ({
          ...prev,
          mobile: "This phone number already exists in the system",
        }));
        setErr("⚠️ This phone number already exists in the system.");
      } else if (errorResponse?.field) {
        setFieldErrors((prev) => ({ ...prev, [errorResponse.field]: errorMessage }));
        setErr(errorMessage);
      } else if (errorMessage.toLowerCase().includes('sin') || errorResponse?.error?.toLowerCase().includes('sin')) {
        // Set field-specific error for SIN
        setFieldErrors((prev) => ({
          ...prev,
          sin: errorMessage
        }));
        // Also set general error message
        setErr(errorMessage);
      } else {
        // For other errors, just set the general error message
        setErr(errorMessage);
      }

      return;
    }

    saveRegistrationProvince(currentAccountId, formData.address?.region);
    setSuccess("✅ Profile saved successfully!");
    showSuccess("Form completed successfully!");
    setTimeout(() => navigate("/register"), 1000);
  };

  if (!me) return <div style={{ padding: 24 }}>Loading...</div>;

  return (
    <div className="page-background min-h-screen">
      <div className="profile-form-container">
        <div className="form-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 56, marginBottom: 10 }} />
          <h1 className="text-lg font-semibold mt-4 mb-0" style={{ textAlign: "center" }}>Profile</h1>
        </div>

        <form onSubmit={handleSubmit}>
          {err && <div className="text-red-600 p-2">{err}</div>}
          {success && <div className="text-green-600 p-2">{success}</div>}

          {/* General Information */}
          <div className="form-section">
            <div className="form-row">
              <div className="profile-form-group">
                <label className="profile-form-label">First Name*</label>
                <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} className="profile-form-input" />
                {fieldErrors.first_name && <div className="text-red-600 text-xs mt-1">{fieldErrors.first_name}</div>}
              </div>
              <div className="profile-form-group">
                <label className="profile-form-label">Last Name*</label>
                <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} className="profile-form-input" />
                {fieldErrors.last_name && <div className="text-red-600 text-xs mt-1">{fieldErrors.last_name}</div>}
              </div>
            </div>

            <div className="form-row">
              <div className="profile-form-group">
                <label className="profile-form-label">Middle Name</label>
                <input type="text" name="middle_name" value={formData.middle_name} onChange={handleChange} className="profile-form-input" />
              </div>
              <div className="profile-form-group">
                <label className="profile-form-label">Preferred Name</label>
                <input type="text" name="preferred_name" value={formData.preferred_name} onChange={handleChange} className="profile-form-input" />
                <div className="text-xs text-gray-400 mt-1">Only if different from First Name</div>
              </div>
            </div>

            <div className="profile-form-group">
              <label className="profile-form-label">Email*</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} className="profile-form-input" />
              {fieldErrors.email && <div className="text-red-600 text-xs mt-1">{fieldErrors.email}</div>}
            </div>

            <div className="form-row">
              <div className="profile-form-group">
                <label className="profile-form-label">Gender</label>
                <select name="gender" value={formData.gender} onChange={handleChange} className="profile-form-select">
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="profile-form-group">
                <label className="profile-form-label">Date of Birth</label>
                <DateInput name="dob" value={formData.dob} onChange={(value) => handleChange({ target: { name: 'dob', value } })} className="profile-form-input" />
              </div>
            </div>

            <div className="avatar-wrapper">
              <label>Profile Picture</label>
              <div className="avatar-container">
                <StaffAvatarCircle
                  accountId={currentAccountId}
                  employee={{
                    account_id: currentAccountId,
                    name:
                      [formData.first_name, formData.last_name]
                        .filter(Boolean)
                        .join(" ")
                        .trim() || formData.preferred_name,
                    profile_picture: formData.profile_picture_preview || formData.profile_picture,
                    initials:
                      `${formData.first_name?.[0] || ""}${formData.last_name?.[0] || ""}`.toUpperCase() ||
                      "NA",
                  }}
                  className="avatar-img"
                  textClassName="text-sm font-semibold text-white"
                  emptyLabel="NA"
                />

                <label htmlFor="avatarInput" className="avatar-edit-outside">📷</label>

                <input
                  id="avatarInput"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const base64 = reader.result;
                      const updated = { ...formData, profile_picture: base64, profile_picture_preview: base64 };
                      setFormData(updated);
                      localStorage.setItem(`form_${currentAccountId}_${contentId}`, JSON.stringify(updated));
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="form-section">
            <div className="profile-form-group">
              <label className="profile-form-label">Mobile number on record with Talent*</label>
              <div className="profile-form-input phone-input-container">
                <PhoneInput
                  value={mobile}
                  onChange={(value) => {
                    setMobile(value);
                    const updated = { ...formData, mobile: value };
                    setFormData(updated);
                    localStorage.setItem(`form_${currentAccountId}_${contentId}`, JSON.stringify(updated));
                    setFieldErrors((prev) => {
                      if (!prev.mobile) return prev;
                      const next = { ...prev };
                      if (value && isValidPhoneNumber(value)) delete next.mobile;
                      return next;
                    });
                  }}
                  onCountryChange={(country) => {
                    if (!country) return;
                    const code = `+${getCountryCallingCode(country)}`;
                    // If empty or not starting with selected code, initialize with calling code
                    setMobile((prev) => {
                      const next = !prev || !String(prev).startsWith(code) ? code : prev;
                      const updated = { ...formData, mobile: next };
                      setFormData(updated);
                      localStorage.setItem(`form_${currentAccountId}_${contentId}`, JSON.stringify(updated));
                      return next;
                    });
                  }}
                  international
                  withCountryCallingCode
                  countryCallingCodeEditable={false}
                  defaultCountry="CA"
                  className="w-full"
                />
              </div>
              {fieldErrors.mobile && <div className="text-red-600 text-xs mt-1">{fieldErrors.mobile}</div>}
            </div>

            <div className="profile-form-group">
              <label className="profile-form-label">Address*</label>
              <AddressAutocomplete
                value={normalizeAddress(formData.address)}
                onChange={(val) => {
                  const updatedData = {
                    ...formData,
                    address: normalizeAddress(val),
                  };
                  setFormData(updatedData);
                  localStorage.setItem(`form_${currentAccountId}_${contentId}`, JSON.stringify(updatedData));
                  setFieldErrors((prev) => {
                    if (!prev.address) return prev;
                    const next = { ...prev };
                    delete next.address;
                    return next;
                  });
                }}
              />
              {fieldErrors.address && <div className="text-red-600 text-xs mt-1">{fieldErrors.address}</div>}
            </div>

            <div className="profile-form-group">
              <label className="profile-form-label">Emergency Contact Name*</label>
              <input type="text" name="emergency_name" value={formData.emergency_name} onChange={handleChange} className="profile-form-input" />
              {fieldErrors.emergency_name && <div className="text-red-600 text-xs mt-1">{fieldErrors.emergency_name}</div>}
            </div>

            <div className="profile-form-group">
              <label className="profile-form-label">Emergency Contact Number*</label>
              <div className="profile-form-input phone-input-container">
                <PhoneInput
                  value={emergency}
                  onChange={(value) => {
                    setEmergency(value);
                    const updated = { ...formData, emergency_number: value };
                    setFormData(updated);
                    localStorage.setItem(`form_${currentAccountId}_${contentId}`, JSON.stringify(updated));
                    setFieldErrors((prev) => {
                      if (!prev.emergency_number) return prev;
                      const next = { ...prev };
                      if (value && isValidPhoneNumber(value)) delete next.emergency_number;
                      return next;
                    });
                  }}
                  onCountryChange={(country) => {
                    if (!country) return;
                    const code = `+${getCountryCallingCode(country)}`;
                    setEmergency((prev) => {
                      const next = !prev || !String(prev).startsWith(code) ? code : prev;
                      const updated = { ...formData, emergency_number: next };
                      setFormData(updated);
                      localStorage.setItem(`form_${currentAccountId}_${contentId}`, JSON.stringify(updated));
                      return next;
                    });
                  }}
                  international
                  withCountryCallingCode
                  countryCallingCodeEditable={false}
                  defaultCountry="CA"
                  className="w-full"
                />
              </div>
              {fieldErrors.emergency_number && <div className="text-red-600 text-xs mt-1">{fieldErrors.emergency_number}</div>}
            </div>
          </div>

          {/* Payroll Information */}
          <div className="form-section">
            <div className="profile-form-group">
              <label className="profile-form-label">Residency Status*</label>
              <select name="residency" value={formData.residency} onChange={handleChange} className="profile-form-select">
                <option value="">Select Status</option>
                <option value="citizen">Canadian Citizen / Permanent Resident</option>
                <option value="work_permit">Work Permit</option>
                <option value="study_permit">Study Permit</option>
                <option value="refugee">Refugee</option>
              </select>
              {fieldErrors.residency && <div className="text-red-600 text-xs mt-1">{fieldErrors.residency}</div>}
            </div>

            {/* Residency document upload for Work / Study Permit */}
            {/* {(formData.residency === 'work_permit' || formData.residency === 'study_permit' || formData.residency === 'refugee') && ( 
              <div className="profile-form-group">
                <label className="profile-form-label">
                  {formData.residency === 'work_permit' && 'Work Permit *'}
                  {formData.residency === 'study_permit' && 'Study Permit *'}
                  {formData.residency === 'refugee' && 'Work Permit *'}
                </label>

                <div
                  className="residency-upload-box profile-form-input"
                  onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) handleResidencyFile(f); }}
                  onDragOver={(e) => e.preventDefault()}
                  style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8 }}
                >
                  <div className="text-muted text-sm">Drag & Drop your file or use the button below</div>
                  <div className="text-xs text-muted">{formData.residency_document_name || 'No file selected'}</div>
                  <input
                    id="residencyFile"
                    type="file"
                    accept={STAFF_DOCUMENT_ACCEPT}
                    style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) handleResidencyFile(f); }}
                  />
                  <label htmlFor="residencyFile" className="mt-2 inline-block px-4 py-2 bg-buttonRed text-white rounded-3xl cursor-pointer">Upload File</label>
                  {fieldErrors.residency_document && <div className="text-red-600 text-xs mt-1">{fieldErrors.residency_document}</div>}
                </div>
              </div>
            )} */}

            {(formData.residency === 'work_permit' || formData.residency === 'study_permit' || formData.residency === 'refugee') && (
              <div className="profile-form-group">
                <label className="profile-form-label">
                  {formData.residency === 'work_permit' && 'Work Permit *'}
                  {formData.residency === 'study_permit' && 'Study Permit *'}
                  {formData.residency === 'refugee' && 'Work Permit *'}
                </label>

                <div
                  className="residency-upload-box profile-form-input"
                  onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) handleResidencyFile(f); }}
                  onDragOver={(e) => e.preventDefault()}
                  style={{
                    minHeight: 140,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 8,
                    position: 'relative',
                    padding: '20px'
                  }}
                >
                  <div className="text-muted text-sm text-center">
                    Drag & Drop to {formData.residency_document ? "Re-upload" : "Upload"} <br /> or use the buttons below
                  </div>

                  <div className="text-sm font-semibold text-gray-700">
                    {formData.residency_document_name ? `📄 ${formData.residency_document_name}` : 'No file selected'}
                  </div>

                  <input
                    id="residencyFile"
                    type="file"
                    accept={STAFF_DOCUMENT_ACCEPT}
                    style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) handleResidencyFile(f); }}
                  />

                  {/* LEFT BUTTON: Upload / Re-upload  */}
                  <label
                    htmlFor="residencyFile"
                    title="Upload / Re-upload"
                    className="bg-buttonRed text-white hover:opacity-90 transition-all shadow-md"
                    style={{
                      position: 'absolute',
                      bottom: '10px',
                      left: '10px',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      zIndex: 10
                    }}
                  >
                    <FaUpload size={20} />
                  </label>

                  {uploading && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center">
                      <div className="flex items-center gap-2 rounded-full bg-black/70 px-4 py-2 text-sm text-white shadow-lg">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                        Uploading...
                      </div>
                    </div>
                  )}

                  {/* --- RIGHT BUTTON: Download (Only shows if file exists) --- */}
                  {formData.residency_document && (
                    <div
                      onClick={handleDownload}
                      title="Download File"
                      className="bg-buttonRed text-white hover:opacity-90 transition-all shadow-md"
                      style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '10px',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 10
                      }}
                    >
                      <FaDownload size={20} />
                    </div>
                  )}

                  {fieldErrors.residency_document && <div className="text-red-600 text-xs mt-1">{fieldErrors.residency_document}</div>}
                </div>

                <label className="profile-form-label mt-3">Document Expiry Date *</label>
                <DateInput
                  name="residency_document_expiry"
                  value={formData.residency_document_expiry}
                  onChange={(value) => handleChange({ target: { name: 'residency_document_expiry', value } })}
                  className="profile-form-input"
                />
                {fieldErrors.residency_document_expiry && <div className="text-red-600 text-xs mt-1">{fieldErrors.residency_document_expiry}</div>}
              </div>
            )}


            <div className="profile-form-group">
              <label className="profile-form-label">Social Insurance Number{formData.residency && '*'}</label>
              <input
                type="text"
                name="sin"
                value={formData.sin}
                onChange={handleChange}
                className="profile-form-input"
                inputMode="numeric"
                autoComplete="off"
                maxLength={11}
                placeholder="123 456 789"
              />
              {fieldErrors.sin && <div className="text-red-600 text-xs mt-1">{fieldErrors.sin}</div>}

              {formData.residency && (
                <div className={`mt-3 rounded-lg border p-3 text-sm ${fieldErrors.sin_disclaimer_agreed ? "border-red-400 bg-red-50" : "border-gray-200 bg-gray-50"}`}>
                  <p className="text-gray-600">{SIN_DISCLAIMER_TEXT}</p>
                  <label className={`mt-2 flex items-start gap-2 ${normalizeSin(formData.sin) ? "cursor-not-allowed text-gray-400" : "cursor-pointer text-gray-800"}`}>
                    <input
                      type="checkbox"
                      name="sin_disclaimer_agreed"
                      className="mt-1 shrink-0"
                      checked={!!formData.sin_disclaimer_agreed}
                      onChange={handleChange}
                      disabled={!!normalizeSin(formData.sin)}
                    />
                    <span className="font-medium">{SIN_DISCLAIMER_AGREE_TEXT}{!normalizeSin(formData.sin) && '*'}</span>
                  </label>
                  {normalizeSin(formData.sin) && <div className="mt-1 text-xs text-gray-500">Not required &mdash; SIN provided.</div>}
                  {fieldErrors.sin_disclaimer_agreed && <div className="text-red-600 text-xs mt-1">{fieldErrors.sin_disclaimer_agreed}</div>}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="form-actions">
            <button type="button" className="btn btn-secondary mb-4" onClick={() => navigate("/register")}>BACK</button>
            <button type="submit" className="btn btn-primary mb-4">SAVE</button>
          </div>
        </form>
      </div>
    </div>
  );
}
